/** Shared labels / icons for login multi-method verify UI */

export const METHOD_TYPE = {
  EMAIL: 1,
  AUTHENTICATOR: 2,
  PHONE: 3,
  PASSKEY: 4,
};

export function methodDisplayName(type) {
  switch (Number(type)) {
    case METHOD_TYPE.EMAIL:
      return "Email";
    case METHOD_TYPE.PHONE:
      return "Phone Number";
    case METHOD_TYPE.AUTHENTICATOR:
      return "Authenticator App";
    case METHOD_TYPE.PASSKEY:
      return "Passkeys";
    default:
      return "Verification";
  }
}

export function methodIconClass(type) {
  switch (Number(type)) {
    case METHOD_TYPE.EMAIL:
      return "ri-mail-line";
    case METHOD_TYPE.PHONE:
      return "ri-smartphone-line";
    case METHOD_TYPE.AUTHENTICATOR:
      return "ri-shield-keyhole-line";
    case METHOD_TYPE.PASSKEY:
      return "ri-fingerprint-line";
    default:
      return "ri-shield-check-line";
  }
}

export function methodCodeTitle(type) {
  switch (Number(type)) {
    case METHOD_TYPE.EMAIL:
      return "Verify Your Email";
    case METHOD_TYPE.PHONE:
      return "Verify Your Phone";
    case METHOD_TYPE.AUTHENTICATOR:
      return "Verify with authenticator";
    case METHOD_TYPE.PASSKEY:
      return "Passkey Authentication";
    default:
      return "Verification";
  }
}

export function methodNeedsOtpSend(type) {
  const t = Number(type);
  return t === METHOD_TYPE.EMAIL || t === METHOD_TYPE.PHONE;
}

export function methodPillLabel(method, { active, done } = {}) {
  const base = method?.label || methodDisplayName(method?.type);
  if (done) return `${base} ✓`;
  if (active) return `${base} (now)`;
  return base;
}

/** Normalize API recovery keys to totp | email | phone. */
export function normalizeRecoveryMethodKey(raw) {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (s === "totp" || s === "google" || s === "authenticator" || s === "app" || s === "ga") return "totp";
  if (s === "email" || s === "mail") return "email";
  if (s === "phone" || s === "mobile" || s === "sms") return "phone";
  return "";
}

export function normalizeRecoverableMethodKeys(raw) {
  if (!Array.isArray(raw)) return [];
  const keys = raw.map(normalizeRecoveryMethodKey).filter(Boolean);
  return [...new Set(keys)];
}

export function recoveryKeyToMethodType(key) {
  const k = normalizeRecoveryMethodKey(key);
  if (k === "email") return METHOD_TYPE.EMAIL;
  if (k === "totp") return METHOD_TYPE.AUTHENTICATOR;
  if (k === "phone") return METHOD_TYPE.PHONE;
  return 0;
}

export function methodTypeToRecoveryKey(type) {
  const t = Number(type);
  if (t === METHOD_TYPE.EMAIL) return "email";
  if (t === METHOD_TYPE.AUTHENTICATOR) return "totp";
  if (t === METHOD_TYPE.PHONE) return "phone";
  return "";
}

export function recoveryCantAccessLabel(key) {
  const k = normalizeRecoveryMethodKey(key);
  if (k === "totp") return "Can't access authenticator?";
  if (k === "email") return "Can't access email?";
  if (k === "phone") return "Can't access this phone?";
  return "Can't access this method?";
}

export function recoveryLostMethodLabel(key) {
  const k = normalizeRecoveryMethodKey(key);
  if (k === "totp") return "authenticator";
  if (k === "email") return "email";
  if (k === "phone") return "phone";
  return "method";
}

export function recoveryLostMethodTitle(key) {
  const k = normalizeRecoveryMethodKey(key);
  if (k === "totp") return "Google Authenticator";
  if (k === "email") return "Email OTP";
  if (k === "phone") return "Phone Number";
  return "this method";
}

export function recoveryRestrictionHours(raw) {
  const hours = Number(raw?.restrictionHours ?? raw?.restriction_hours ?? raw);
  return Number.isFinite(hours) && hours > 0 ? hours : 24;
}

/** Last recovery verify succeeded (tokens issued, or no remaining proofs). */
export function isRecoveryVerifyComplete(result) {
  const data = result?.data && typeof result.data === "object" && !Array.isArray(result.data) ? result.data : {};
  if (data.verification_complete === true || data.verification_complete === "true") return true;
  if (data.token || data.access_token || data.disabled_method) return true;
  const remaining = normalizeRecoverableMethodKeys(data.remaining_methods);
  if (data.verification_complete === false) return remaining.length === 0;
  if (result?.success && Array.isArray(data.remaining_methods) && remaining.length === 0) return true;
  return false;
}

export function recoveryVerifyErrorMessage(result) {
  const code = String(result?.code || "");
  const raw = String(result?.message || "").trim();
  if (code === "METHOD_RECOVERY_INCOMPLETE" || /prove all remaining methods/i.test(raw) || /prove the remaining methods/i.test(raw)) {
    return "Could not finish removing this method. Please try the code again.";
  }
  return raw || "Invalid verification code. Please try again.";
}

export function recoveryMethodObject(key, availableMethods = []) {
  const type = recoveryKeyToMethodType(key);
  if (!type) return null;
  const existing = (availableMethods || []).find((m) => Number(m?.type) === type);
  if (existing) return existing;
  return {
    type,
    label: methodDisplayName(type),
    icon: methodIconClass(type),
    description: methodCodeTitle(type),
  };
}
